/**
 * Frontend configuration for technical indicators by granularity.
 *
 * THIS MUST MATCH the backend GranularityMetricConfig.java exactly.
 * When backend config changes, this file MUST be updated to match.
 *
 * @see crypto-trading-backend/src/main/java/com/svjubilee/trading/crypto/coinbase/model/metrics/GranularityMetricConfig.java
 */

export type MetricType = 'SMA' | 'EMA' | 'RSI' | 'VOLUME_EMA' | 'VOLUME_SMA' | 'OBV' | 'ATR' | 'ADX' | 'BOLLINGER_UPPER' | 'BOLLINGER_MIDDLE' | 'BOLLINGER_LOWER' | 'MACD' | 'VWAP' | 'VWAP_UPPER' | 'VWAP_LOWER' | 'TENKAN_SEN' | 'KIJUN_SEN' | 'SENKOU_SPAN_A' | 'SENKOU_SPAN_B' | 'CHIKOU_SPAN';

export interface GranularityMetrics {
  SMA?: number[];
  EMA?: number[];
  RSI?: number[];
  VOLUME_EMA?: number[];
  VOLUME_SMA?: number[];
  OBV?: boolean;
  ATR?: number[];
  ADX?: number[];
  BOLLINGER?: boolean; // Bollinger bands are always period 20
  MACD?: boolean; // MACD is 12/26/9
  VWAP?: boolean; // VWAP + ±1σ bands, always period 20; service scales by band_sigma at signal time
  ICHIMOKU?: boolean; // Ichimoku Cloud five components — TENKAN(9), KIJUN(26), SENKOU_A(26), SENKOU_B(52), CHIKOU(26); displacement applied at read time per AD-006
}

/**
 * Configuration mapping - matches backend GranularityMetricConfig.java
 *
 * NOTE: This configuration must be kept in sync with the backend.
 * Last synced: 2026-05-21
 */
const GRANULARITY_METRICS: Record<number, GranularityMetrics> = {
  // 1-Minute Candles
  // Backend: SMA 9, 21 | EMA 9, 20, 21, 50, 200 | RSI 14 | Volume EMA/SMA 20 | OBV | ATR 14 | ADX 14
  1: {
    SMA: [9, 21],
    EMA: [9, 20, 21, 50, 200],
    RSI: [14],
    VOLUME_EMA: [20],
    VOLUME_SMA: [20],
    OBV: true,
    ATR: [14],
    ADX: [14],
  },

  // 5-Minute Candles
  // Backend: SMA 20, 50, 200 | EMA 9, 20, 21, 50, 200 | RSI 14 | Volume EMA/SMA 20 | OBV | ATR 14 | ADX 14 | Bollinger 20 | VWAP 20 | MACD
  5: {
    SMA: [20, 50, 200],
    EMA: [9, 20, 21, 50, 200],
    RSI: [14],
    VOLUME_EMA: [20],
    VOLUME_SMA: [20],
    OBV: true,
    ATR: [14],
    ADX: [14],
    BOLLINGER: true,
    VWAP: true,
  },

  // 15-Minute Candles
  // Backend: SMA 20, 50, 200 | EMA 20, 50, 200 | RSI 14 | Volume EMA/SMA 20 | OBV | ATR 14 | ADX 14 | Bollinger 20 | VWAP 20 | MACD
  15: {
    SMA: [20, 50, 200],
    EMA: [20, 50, 200],
    RSI: [14],
    VOLUME_EMA: [20],
    VOLUME_SMA: [20],
    OBV: true,
    ATR: [14],
    ADX: [14],
    BOLLINGER: true,
    VWAP: true,
  },

  // 30-Minute Candles
  // Backend: SMA 20, 50, 200 | EMA 20, 50, 200 | RSI 14 | Volume EMA/SMA 20 | OBV | ATR 14 | ADX 14 | Bollinger 20 | VWAP 20 | MACD
  30: {
    SMA: [20, 50, 200],
    EMA: [20, 50, 200],
    RSI: [14],
    VOLUME_EMA: [20],
    VOLUME_SMA: [20],
    OBV: true,
    ATR: [14],
    ADX: [14],
    BOLLINGER: true,
    VWAP: true,
  },

  // 1-Hour Candles
  // Backend: SMA 50, 100, 200 | EMA 20, 50, 100, 200 | RSI 14 | Volume EMA/SMA 20 | OBV | ATR 14 | ADX 14 | Bollinger 20 | VWAP 20 | MACD | Ichimoku 9/26/52/26
  60: {
    SMA: [50, 100, 200],
    EMA: [20, 50, 100, 200],
    RSI: [14],
    VOLUME_EMA: [20],
    VOLUME_SMA: [20],
    OBV: true,
    ATR: [14],
    ADX: [14],
    BOLLINGER: true,
    VWAP: true,
    ICHIMOKU: true,
  },

  // 4-Hour Candles
  // Backend: SMA 50, 200 | EMA 20, 50, 100, 200 | RSI 14 | Volume EMA/SMA 20 | OBV | ATR 14 | ADX 14 | MACD
  240: {
    SMA: [50, 200],
    EMA: [20, 50, 100, 200],
    RSI: [14],
    VOLUME_EMA: [20],
    VOLUME_SMA: [20],
    OBV: true,
    ATR: [14],
    ADX: [14],
  },

  // 1-Day Candles
  // Backend: SMA 50, 200 | EMA 20, 50, 200 | RSI 14 | Volume EMA/SMA 20 | OBV | ATR 14 | ADX 14 | MACD
  1440: {
    SMA: [50, 200],
    EMA: [20, 50, 200],
    RSI: [14],
    VOLUME_EMA: [20],
    VOLUME_SMA: [20],
    OBV: true,
    ATR: [14],
    ADX: [14],
  },
};

/**
 * Get all metrics configured for a specific granularity
 */
export function getMetricsForGranularity(granularityMins: number): GranularityMetrics {
  const config = GRANULARITY_METRICS[granularityMins];
  if (!config) {
    console.warn(`No metric configuration found for granularity ${granularityMins} minutes. Using 15-minute defaults.`);
    return GRANULARITY_METRICS[15];
  }
  return config;
}

/**
 * Get the periods for a specific metric type and granularity
 */
export function getPeriodsForMetric(granularityMins: number, metricType: MetricType): number[] {
  const config = getMetricsForGranularity(granularityMins);

  switch (metricType) {
    case 'SMA':
      return config.SMA || [];
    case 'EMA':
      return config.EMA || [];
    case 'RSI':
      return config.RSI || [];
    case 'VOLUME_EMA':
      return config.VOLUME_EMA || [];
    case 'VOLUME_SMA':
      return config.VOLUME_SMA || [];
    case 'ATR':
      return config.ATR || [];
    case 'ADX':
      return config.ADX || [];
    default:
      return [];
  }
}

/**
 * Check if a specific indicator is available for a granularity
 */
export function isIndicatorAvailable(granularityMins: number, metricType: MetricType, period?: number): boolean {
  const config = getMetricsForGranularity(granularityMins);

  switch (metricType) {
    case 'SMA':
      return period ? (config.SMA || []).includes(period) : (config.SMA?.length || 0) > 0;
    case 'EMA':
      return period ? (config.EMA || []).includes(period) : (config.EMA?.length || 0) > 0;
    case 'RSI':
      return period ? (config.RSI || []).includes(period) : (config.RSI?.length || 0) > 0;
    case 'VOLUME_EMA':
      return period ? (config.VOLUME_EMA || []).includes(period) : (config.VOLUME_EMA?.length || 0) > 0;
    case 'VOLUME_SMA':
      return period ? (config.VOLUME_SMA || []).includes(period) : (config.VOLUME_SMA?.length || 0) > 0;
    case 'OBV':
      return config.OBV || false;
    case 'ATR':
      return period ? (config.ATR || []).includes(period) : (config.ATR?.length || 0) > 0;
    case 'ADX':
      return period ? (config.ADX || []).includes(period) : (config.ADX?.length || 0) > 0;
    case 'BOLLINGER_UPPER':
    case 'BOLLINGER_MIDDLE':
    case 'BOLLINGER_LOWER':
      return config.BOLLINGER || false;
    case 'MACD':
      return config.MACD || false;
    case 'VWAP':
    case 'VWAP_UPPER':
    case 'VWAP_LOWER':
      return config.VWAP || false;
    case 'TENKAN_SEN':
    case 'KIJUN_SEN':
    case 'SENKOU_SPAN_A':
    case 'SENKOU_SPAN_B':
    case 'CHIKOU_SPAN':
      return config.ICHIMOKU || false;
    default:
      return false;
  }
}

/**
 * Check if Ichimoku Cloud components are available for a granularity
 */
export function isIchimokuAvailable(granularityMins: number): boolean {
  const config = getMetricsForGranularity(granularityMins);
  return config.ICHIMOKU || false;
}

/**
 * Check if Bollinger Bands are available for a granularity
 */
export function isBollingerAvailable(granularityMins: number): boolean {
  const config = getMetricsForGranularity(granularityMins);
  return config.BOLLINGER || false;
}

/**
 * Check if MACD is available for a granularity
 */
export function isMacdAvailable(granularityMins: number): boolean {
  const config = getMetricsForGranularity(granularityMins);
  return config.MACD || false;
}

/**
 * Check if OBV is available for a granularity
 */
export function isObvAvailable(granularityMins: number): boolean {
  const config = getMetricsForGranularity(granularityMins);
  return config.OBV || false;
}

/**
 * Check if VWAP (and its ±1σ bands) is available for a granularity
 */
export function isVwapAvailable(granularityMins: number): boolean {
  const config = getMetricsForGranularity(granularityMins);
  return config.VWAP || false;
}

/**
 * Get all available SMA periods for a granularity
 */
export function getAvailableSmaPeriods(granularityMins: number): number[] {
  return getPeriodsForMetric(granularityMins, 'SMA');
}

/**
 * Get all available EMA periods for a granularity
 */
export function getAvailableEmaPeriods(granularityMins: number): number[] {
  return getPeriodsForMetric(granularityMins, 'EMA');
}

/**
 * Get all available moving average periods (both SMA and EMA) for a granularity
 */
export function getAvailableMovingAveragePeriods(granularityMins: number): { SMA: number[]; EMA: number[] } {
  return {
    SMA: getAvailableSmaPeriods(granularityMins),
    EMA: getAvailableEmaPeriods(granularityMins),
  };
}

// ---------------------------------------------------------------------------
// Strategy-metric-period validity (metric-gated period selection)
//
// A StrategyMetricPeriod maps to a candle granularity; a period is VALID for a
// strategy iff every metric the strategy's entry signal reads is computed at
// that granularity. This mirrors the backend validity rule
// (StrategyMetricPeriodValidity over GranularityMetricConfig) — keep both in
// lockstep (CLAUDE.md hidden-gate discipline).
// ---------------------------------------------------------------------------

export type StrategyMetricPeriodName =
  | 'ULTRA_SHORT' | 'VERY_SHORT' | 'SHORT' | 'MEDIUM_SHORT' | 'MEDIUM' | 'LONG' | 'VERY_LONG';

/** Single-position strategy discriminators (subset of backend TradeGroupType). */
export type SinglePositionStrategy =
  | 'RSI_EXTREMES' | 'MACD_REVERSION' | 'MACD_MOMENTUM' | 'BOLLINGER_REVERSION' | 'VWAP_REVERSION' | 'ICHIMOKU_CLOUD'
  | 'PIVOT_POINT_TRADING' | 'MA_CROSSOVER' | 'SUPPORT_RETEST_DIP_LADDER';

/** Candle granularity (minutes) each period reads its metrics at. */
export const STRATEGY_METRIC_PERIOD_GRANULARITY: Record<StrategyMetricPeriodName, number> = {
  ULTRA_SHORT: 1,
  VERY_SHORT: 5,
  SHORT: 15,
  MEDIUM_SHORT: 30,
  MEDIUM: 60,
  LONG: 240,
  VERY_LONG: 1440,
};

/** Canonical period ordering (shortest → longest), used to order dropdowns. */
export const STRATEGY_METRIC_PERIOD_ORDER: StrategyMetricPeriodName[] = [
  'ULTRA_SHORT', 'VERY_SHORT', 'SHORT', 'MEDIUM_SHORT', 'MEDIUM', 'LONG', 'VERY_LONG',
];

/**
 * Whether a strategy's full required-metric set is computed at the given granularity.
 * MACD_REVERSION reads MACD + RSI; MACD is dispatched at every granularity by the
 * backend candle fetcher (not gated by the config map), so RSI availability is the
 * effective gate. RSI is computed at every supported granularity, so RSI/MACD are
 * valid everywhere; Bollinger/VWAP/Ichimoku follow their per-granularity flags.
 */
function strategyMetricsAvailable(strategy: SinglePositionStrategy, granularityMins: number): boolean {
  const config = getMetricsForGranularity(granularityMins);
  const rsiAvailable = (config.RSI?.length || 0) > 0;
  switch (strategy) {
    case 'RSI_EXTREMES':
      return rsiAvailable;
    case 'MACD_REVERSION':
      return rsiAvailable;
    case 'MACD_MOMENTUM':
      // Mirrors backend StrategyMetricPeriodValidity(MACD_MOMENTUM) =
      // Set.of(CalculatedMetricType.RSI): MACD is dispatched at every granularity
      // by the candle fetcher (not gated by the config map), so RSI availability is
      // the effective gate — valid everywhere RSI is computed (constraint #7, TASK-003).
      return rsiAvailable;
    case 'BOLLINGER_REVERSION':
      return config.BOLLINGER || false;
    case 'VWAP_REVERSION':
      return config.VWAP || false;
    case 'ICHIMOKU_CLOUD':
      return config.ICHIMOKU || false;
    case 'PIVOT_POINT_TRADING':
      // PIVOT_POINT_TRADING reads pivot levels from metrics.pivot_points (NOT gated
      // by this config map); only its technical_indicators dependencies gate period
      // validity: RSI (oversold confirmation) and ATR (position sizing). Both are
      // computed at every supported granularity, so all periods are valid. Mirrors
      // backend StrategyMetricPeriodValidity.REQUIRED_METRICS.PIVOT_POINT_TRADING.
      return rsiAvailable && (config.ATR?.length || 0) > 0;
    case 'MA_CROSSOVER':
      // Mirrors backend StrategyMetricPeriodValidity(MA_CROSSOVER) =
      // Set.of(CalculatedMetricType.EMA, CalculatedMetricType.SMA): the entry signal
      // reads moving averages, so a period is valid iff BOTH SMA and EMA series are
      // computed at that granularity. Both are present at every supported granularity
      // (TASK-001 pinned the flagship 50/200 EMA at 15-min and 1-hour), so every period
      // is valid — matching the backend containsAll({EMA, SMA}) check exactly.
      return (config.SMA?.length || 0) > 0 && (config.EMA?.length || 0) > 0;
    case 'SUPPORT_RETEST_DIP_LADDER':
      // SRDL reads raw candles, not calculated metrics gated by this config map — it has
      // no metric dependency to check here. Mirrors backend StrategyMetricPeriodValidity's
      // FIXED_PERIODS entry (SUPPORT_RETEST_DIP_LADDER -> MEDIUM_SHORT): exactly one
      // granularity is valid, matching the Ichimoku precedent of a launch-locked single
      // period (there ICHIMOKU_CLOUD's boolean flag happens to be true at only one
      // granularity; here the strategy is pinned to MEDIUM_SHORT directly).
      return granularityMins === STRATEGY_METRIC_PERIOD_GRANULARITY.MEDIUM_SHORT;
    default:
      return false;
  }
}

/**
 * The StrategyMetricPeriods a given strategy may be created with — only those
 * whose granularity has the strategy's required metrics computed.
 */
export function validPeriodsFor(strategy: SinglePositionStrategy): StrategyMetricPeriodName[] {
  return STRATEGY_METRIC_PERIOD_ORDER.filter((period) =>
    strategyMetricsAvailable(strategy, STRATEGY_METRIC_PERIOD_GRANULARITY[period]),
  );
}
